import React from 'react'
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Container,
  Typography,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import isEqual from 'lodash/isEqual'
import {
  CallToActionTags,
  CTA as CallToActionType,
  CTAVariants,
  ResumeSamples,
} from '../../types/cta'
import useEvents from '../../hooks/useEvents'
import useUrlPathBuilder from '../../hooks/useUrlPathBuilder'
import { createContentfulClassGetter } from '../../utils/contentful'
import { BindToFields } from '../../types/contentfulAttributes'
import { withHideOn } from '../../hocs/withHideOn'
import { getTextColor, getColor } from '../../utils/colors'
import RichText from '../RichText'
import NavigationGroupLink from '../NavigationGroup/NavigationGroupLink'
import TrustPilotReviewCountCard from '../TrustPilot/ReviewCountCard'
import SentryErrorBoundary from '../Errors/SentryErrorBoundary'
import Image from '../ContentfulEntries/Image'
import Carousel from '../LandingPage/Carousel'
import FullWidthFooterCallToAction from './FullWidthFooter'
import Link from '../Link'

type PropClasses = Partial<{
  root: string
  image: string
  cardContent: string
  header: string
  text: string
  actions: string
}>

export interface CallToActionProps {
  cta: CallToActionType & {
    classes?: PropClasses
  }
  inPost?: boolean
  classes?: PropClasses
  sectionClass?: string
}

const CallToAction: React.FC<CallToActionProps> = ({
  cta,
  inPost = false,
  sectionClass,
  classes: propClasses = {},
}) => {
  const { classes, cx } = useStyles({ cta })
  const sendEvent = useEvents()
  const defaultCta = !cta.variant || cta.variant === CTAVariants.Default
  const { blogPrefix, urlPathBuilder } = useUrlPathBuilder()
  const applyContentfulStyles = createContentfulClassGetter(cta.css)

  const onCallToActionButtonClick = (buttonID: string) => () =>
    sendEvent({
      event: 'Call To Action Button Clicked',
      gtm: true,
      variables: {
        contentful: {
          callToActionID: cta.contentful_id,
          buttonID,
        },
      },
    })

  if (cta.classes) {
    propClasses = { ...propClasses, ...cta.classes }
  }

  if (cta.variant === CTAVariants.FullWidthFooter) {
    return <FullWidthFooterCallToAction cta={cta} classes={propClasses} />
  }

  return (
    <SentryErrorBoundary>
      <Card
        className={cx(
          classes.background,
          {
            [classes.cardInPost]: inPost,
            [classes.card]: !!defaultCta || !!CTAVariants.Drawer,
            [classes.cardSidebar]: cta.variant === CTAVariants.Sidebar,
            [classes.cardCorner]: cta.variant === CTAVariants.CornerImage,
            [classes.cardGrid]: cta.variant === CTAVariants.Card,
            [classes.cardWithCarousel]: cta.variant === CTAVariants.Carousel,
            [classes.cardInDrawer]: cta.variant === CTAVariants.Drawer,
            [classes.cardUpload]: cta.variant === CTAVariants.UploadPage,
            [String(sectionClass)]:
              !!sectionClass && cta.variant === CTAVariants.UploadPage,
          },
          applyContentfulStyles(BindToFields.Main),
          propClasses?.root
        )}
        elevation={0}
        data-contentful-id={cta.contentful_id}
      >
        {cta.image &&
          (cta.variant === CTAVariants.Sidebar ||
            cta.variant === CTAVariants.CornerImage ||
            cta.variant === CTAVariants.Default ||
            cta.variant === CTAVariants.UploadPage) && (
            <Image
              className={cx(
                {
                  [classes.img]: defaultCta,
                  [classes.imgSidebar]: cta.variant === CTAVariants.Sidebar,
                  [classes.imgCorner]: cta.variant === CTAVariants.CornerImage,
                  [classes.imgSidebarServices]:
                    cta.image.contentful_id === '1RqmhCzisH7nWh82nzpH8k',
                },
                propClasses?.image
              )}
              asset={cta.image}
              css={cta.css}
            />
          )}
        <Box
          className={cx({
            [classes.textContainer]: !!defaultCta,
            [classes.textContainerCarousel]:
              cta.variant === CTAVariants.Carousel,
            [classes.textInDrawer]: cta.variant === CTAVariants.Drawer,
            [classes.textContainerSidebar]:
              !!CTAVariants.Sidebar || !!CTAVariants.CornerImage,
          })}
        >
          <CardContent
            className={cx(
              {
                [classes.cardContent]:
                  defaultCta ||
                  cta.variant === CTAVariants.Sidebar ||
                  cta.variant === CTAVariants.CornerImage,
                [classes.cardContentCarousel]:
                  cta.variant === CTAVariants.Carousel,
                [classes.cardContentUpload]:
                  cta.variant === CTAVariants.UploadPage,
              },
              propClasses?.cardContent
            )}
          >
            <Container
              className={cx(
                {
                  [classes.header]: defaultCta,
                  [classes.headerSidebar]: cta.variant === CTAVariants.Sidebar,
                  [classes.headerCorner]:
                    cta.variant === CTAVariants.CornerImage,
                  [classes.headerGrid]: cta.variant === CTAVariants.Card,
                  [classes.headerCarousel]:
                    cta.variant === CTAVariants.Carousel,
                  [classes.headerUpload]:
                    cta.variant === CTAVariants.UploadPage,
                },
                propClasses?.header
              )}
              disableGutters
            >
              <RichText rawBody={cta.header.raw} />
            </Container>

            {cta.text?.raw && (
              <Container
                className={cx(
                  {
                    [classes.text]: defaultCta,
                    [classes.textCorner]:
                      cta.variant === CTAVariants.CornerImage,
                    [classes.textSidebar]: cta.variant === CTAVariants.Sidebar,
                    [classes.textGrid]: cta.variant === CTAVariants.Card,
                    [classes.textUpload]:
                      cta.variant === CTAVariants.UploadPage,
                  },
                  propClasses?.text
                )}
                disableGutters
              >
                <RichText rawBody={cta.text.raw} />
              </Container>
            )}
            {cta.image && cta.variant === CTAVariants.Card && (
              <Image
                className={cx({
                  [classes.img]: defaultCta,
                  [classes.imgGrid]: cta.variant === CTAVariants.Card,
                })}
                asset={cta.image}
              />
            )}
            <CardActions
              className={cx(
                {
                  [classes.cardActions]: defaultCta,
                  [classes.cardActionsCorner]:
                    cta.variant === CTAVariants.CornerImage,
                  [classes.cardActionsSidebar]:
                    cta.variant === CTAVariants.Sidebar,
                  [classes.cardActionsGrid]: cta.variant === CTAVariants.Card,
                  [classes.cardActionsCarousel]:
                    cta.variant === CTAVariants.Carousel,
                },
                propClasses?.actions
              )}
            >
              {cta.buttons.map((btn) => (
                <NavigationGroupLink
                  key={btn.contentful_id}
                  onClick={onCallToActionButtonClick(btn.contentful_id)}
                  link={btn}
                  className={classes.navigationGroupUpload}
                />
              ))}
            </CardActions>
          </CardContent>
        </Box>

        {cta.carouselLinks && cta.variant === CTAVariants.Carousel && (
          <Box className={classes.carousel}>
            <Carousel
              slidesToShow={2}
              dots={false}
              arrowContainerClass={classes.arrowContainer}
              arrowContainerLeftClass={classes.arrowContainerLeft}
              arrowContainerRightClass={classes.arrowContainerRight}
            >
              {cta.carouselLinks.links.map((link: ResumeSamples) => (
                <Link
                  to={urlPathBuilder([blogPrefix, link.slug])}
                  key={link.contentful_id}
                >
                  <Image
                    className={cx({
                      [classes.imgCarousel]:
                        cta.variant === CTAVariants.Carousel,
                    })}
                    asset={link.featuredImages[0]}
                  />
                  <Typography
                    className={classes.carouselTitle}
                    variant={'body1'}
                  >
                    {link.occupation}
                  </Typography>
                </Link>
              ))}
            </Carousel>
          </Box>
        )}

        {cta.tags?.includes(CallToActionTags.TrustPilot) && (
          <Box className={classes.badge}>
            <TrustPilotReviewCountCard />
          </Box>
        )}
      </Card>
    </SentryErrorBoundary>
  )
}

const useStyles = makeStyles<{ cta: CallToActionType }>()((theme, { cta }) => {
  const ctaBgColor = getColor(cta?.backgroundColor ?? '', theme)
  const ctaTextColor = getTextColor(cta?.backgroundColor ?? '', theme)

  return {
    arrowContainer: {
      '&&': {
        border: 'none',
        color: theme.palette.background.default,

        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
    arrowContainerLeft: {
      '&&': {
        left: '-3rem',
      },
    },
    arrowContainerRight: {
      '&&': {
        display: 'none !important' as 'none',
      },
    },
    badge: {
      bottom: '1.5em',
      position: 'absolute',
      right: '2em',

      [theme.breakpoints.down('lg')]: {
        display: 'none',
      },
    },
    card: {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.background.default,
      display: 'flex',
      flexDirection: 'row-reverse',
      padding: '2.5em 2.96em 2.5em 2.5em',
      position: 'relative',
      [theme.breakpoints.down('md')]: {
        textAlign: 'center',
        padding: '2.16em 1.48em 1.69em',
      },
    },
    cardActions: {
      padding: 0,
      [theme.breakpoints.down('md')]: {
        margin: '1.73em 0 1.16em',
        width: '100%',
        '& a': {
          width: '100%',
          '&:not(:first-child)': {
            display: 'none',
          },
        },
        '& button': {
          width: '100%',
          '&:not(:first-child)': {
            display: 'none',
          },
        },
      },
    },
    cardActionsCorner: {
      padding: 0,
      [theme.breakpoints.down('md')]: {
        margin: '0',
        width: '100%',
        '& a': {
          width: '100%',
          '&:not(:first-of-type)': {
            display: 'none',
          },
        },
      },
    },
    cardActionsSidebar: {
      padding: 0,
      width: '100%',

      '& a': {
        width: 'inherit',
      },
      '& button': {
        width: 'inherit',
      },
    },
    cardActionsGrid: {
      justifyContent: 'center',
      marginBottom: '1.8625em',
    },
    cardActionsCarousel: {
      padding: '.5rem 0',
    },
    cardContent: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'space-around',
      padding: '0',
      paddingBottom: '0 !important' as '0',
    },
    cardContentCarousel: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '90%',
      maxWidth: '31.25rem',
      maxHeight: '20rem',
      justifyContent: 'space-around',
      padding: '0',
      [theme.breakpoints.down('md')]: {
        maxWidth: '80%',
      },
    },
    cardContentUpload: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'center',
    },
    cardCorner: {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.background.default,
      display: 'flex',
      flexDirection: 'row-reverse',
      minHeight: '20.69em',
      padding: '2.5em 2.96em 2.5em 2.5em',
      position: 'relative',
      [theme.breakpoints.down('md')]: {
        paddingRight: '27%',
      },
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column-reverse',
        padding: '2.16em 1.48em 1.69em',
        textAlign: 'center',
      },
    },
    cardInPost: {
      marginTop: '2.51em',
    },
    cardSidebar: {
      alignItems: 'center',
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.background.default,
      display: 'flex',
      flexDirection: 'column',
      padding: '2em 1.63em',
      textAlign: 'center',
    },
    cardGrid: {
      alignItems: 'center',
      color: theme.palette.primary.dark,
      backgroundColor: theme.palette.background.default,
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'center',
      padding: '0 3.4375em',
      borderRadius: theme.shape.borderRadius,
    },
    cardUpload: {
      borderRadius: 0,
      paddingTop: '4.11em',
      paddingBottom: '3em',
    },
    cardWithCarousel: {
      borderRadius: theme.shape.borderRadius,
      display: 'flex',
      flexDirection: 'row',
      maxWidth: '100%',
      margin: '2em calc(6% + 7em) 0 calc(6% + 7em)',
      color: theme.palette.background.default,
      backgroundColor: theme.palette.primary.dark,
      padding: '5em 0 5em 2.5em',
      [theme.breakpoints.down('lg')]: {
        margin: '2em 6% 0 6%',
      },
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
      },
      '@media (min-width: 1456px)': {
        margin:
          '2em calc((100% - 91rem)/2 + 5.5rem + 7em) 0 calc((100% - 91rem)/2 + 5.5rem + 7em)',
      },
    },
    cardInDrawer: {
      borderRadius: 0,
      display: 'flex',
      justifyContent: 'center',
      paddingTop: '30px',
    },
    header: {
      '& u': {
        textDecorationColor: theme.palette.primary.main,
      },
      '& *': {
        fontSize: '1.5rem',
        fontWeight: 700,
        lineHeight: 1.21,
      },
      [theme.breakpoints.down('md')]: {
        '& *': {
          fontSize: '1.25rem',
          fontWeight: 400,
          lineHeight: '1.2em',
        },
        '& p': {
          fontSize: '1.25rem',
          fontWeight: 400,
          lineHeight: '1.2em',
        },
        '& u': {
          fontWeight: 700,
        },
      },
    },
    headerCorner: {
      '& *': {
        fontFamily: theme.typography.h1.fontFamily,
        fontSize: '1.81rem',
        fontWeight: 700,
        lineHeight: 1.21,
      },
      [theme.breakpoints.down('md')]: {
        '& *': {
          fontSize: '1.38rem',
          fontWeight: '700',
          lineHeight: '1.2em',
        },
        '& p': {
          fontSize: '1.38rem',
          fontWeight: '700',
          lineHeight: '1.2em',
        },
      },
    },
    headerSidebar: {
      marginTop: '.93em',

      '& *': {
        fontSize: '1.21rem',
        fontWeight: 700,
        lineHeight: 1.21,
      },
    },
    headerGrid: {
      marginTop: '1.8125em',
      '& *': {
        fontFamily: theme.typography.h1.fontFamily,
        fontSize: '1.5em',
        fontWeight: 700,
        lineHeight: 1.17,
      },
    },
    headerCarousel: {
      '& *': {
        fontFamily: theme.typography.h1.fontFamily,
        fontSize: '2.25em',
        fontWeight: 700,
        lineHeight: 1.28,
      },
    },
    headerUpload: {
      width: 'unset',

      '&& *': {
        fontFamily: theme.typography.h1.fontFamily,
        fontSize: '2.81em',
        fontWeight: 700,
        [theme.breakpoints.down('md')]: {
          fontSize: '2.125rem',
          lineHeight: 1.28,
        },
      },
    },
    carouselTitle: {
      marginTop: '.93em',
      color: theme.palette.primary.light,
      '& *': {
        fontSize: '1.21rem',
        fontWeight: 700,
        lineHeight: 1.21,
      },
    },
    textGrid: {
      display: 'none',
    },
    img: {
      height: '100%',
      marginLeft: '8%',
      maxWidth: '15.29em',

      [theme.breakpoints.down('lg')]: {
        '&&': {
          display: 'none',
        },
      },
    },
    imgCorner: {
      height: '100%',
      margin: '0 -12.1em -9.7em 8%',
      maxWidth: '27.1em',
      [theme.breakpoints.down('lg')]: {
        minWidth: '19.1em',
      },
      [theme.breakpoints.down('md')]: {
        bottom: '-5em',
        display: 'initial',
        height: 'auto',
        margin: '0',
        minWidth: 'initial',
        position: 'absolute !important' as 'absolute',
        right: '-17em',
        width: '25.1em',
      },
      [theme.breakpoints.down('sm')]: {
        bottom: 'initial',
        display: 'initial',
        margin: '2em 0 -46% 0',
        minWidth: 'initial',
        position: 'relative !important' as 'relative',
        right: 'initial',
        width: '100%',
      },
    },
    imgSidebar: {
      height: 'initial',
      marginLeft: '0',
      minWidth: 'initial',
      width: '9.38em',
    },
    imgSidebarServices: {
      marginBottom: '-1.5em',
      top: '-2em',
      width: 'calc(100% + 3.26em)',
    },
    imgGrid: {
      width: '85%',
      height: 'initial',
      minWidth: 'initial',
      margin: '1.6em auto 1.8125em',
    },
    carousel: {
      width: '60%',
      marginLeft: '5rem',
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        width: '90vw',
      },

      [theme.breakpoints.down('lg')]: {
        marginLeft: '0',
      },
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
    imgCarousel: {
      background: theme.palette.primary.light,
      width: '90%',
    },
    navigationGroupUpload: {
      '& > .file-drop-target': {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        padding: '2em',
        width: '31.63em',
        [theme.breakpoints.down('md')]: {
          width: '21.375rem',
        },
      },
    },
    text: {
      margin: '.5em 0 1.25em',
      [theme.breakpoints.down('md')]: {
        fontStyle: 'italic',
        marginBottom: '0',
        order: '1',
        '& p': {
          fontSize: '.81em',
          lineHeight: '1.53',
        },
      },
    },
    textCorner: {
      margin: '.5em 0 1.25em',
      [theme.breakpoints.down('md')]: {
        margin: '.72em 0 1.44em',
        '& p': {
          fontSize: '1em',
          lineHeight: '1.5',
        },
      },
    },
    textSidebar: {
      margin: '.25em 0 1.2em',
      '& p': {
        fontSize: '1em',
        lineHeight: '1.38em',
      },
    },
    textInDrawer: {
      // Match the width of the blog post text
      maxWidth: '45.25em',
    },
    textContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-around',
      width: '100%',

      [theme.breakpoints.down('md')]: {
        alignItems: 'center',
        width: '100%',
      },
    },
    textContainerCarousel: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-around',
      width: '100%',
      [theme.breakpoints.down('md')]: {
        width: '100%',
        textAlign: 'left',
        paddingLeft: '2rem',
      },
    },
    textContainerSidebar: {
      width: '100%',
    },
    textUpload: {
      margin: '1.33em 0 2.04em',
      width: 'unset',

      '& > *': {
        fontSize: '1.25em',
        [theme.breakpoints.down('md')]: {
          fontSize: '1.125rem',
          lineHeight: 1.51,
        },
      },
    },
    background: {
      backgroundColor: ctaBgColor,
      color: ctaTextColor,
    },
  }
})

export default React.memo(withHideOn(CallToAction), isEqual)
